<template>
  <div class="calendar-wrapper">
    <div class="kalender-header">
      <h2>{{ $t("global.calendarofevents") }}</h2>
      <div class="legende">
        <b>{{ $t("global.legend") }}</b> <br />
        <div class="frei"><span class="dot"></span>{{ $t("portal.fullyFree") }}</div>
        <div class="teilweise"><span class="dot"></span>{{ $t("portal.partiallyFree") }}</div>
        <div class="reserviert"><span class="dot"></span>{{ $t("portal.reserved") }}</div>
        <div class="voll"><span class="dot"></span>{{ $t("portal.allocated") }}</div>
      </div>
    </div>
    <div class="calendar-header d-flex justify-content-between align-items-center mb-2 pt-4">
      <button class="arrow" @click="prevMonth" :disabled="isBeforeStartMonth">
        <font-awesome-icon icon="fa-solid fa-arrow-left" />
      </button>
      <h3>{{ monthName }} {{ currentYear }}</h3>
      <button class="arrow" @click="nextMonth" :disabled="isAfterEndMonth">
        <font-awesome-icon icon="fa-solid fa-arrow-right" />
      </button>
    </div>
    <div class="calendar-triple d-flex justify-content-between">
      <div v-for="(monthOffset, i) in [-1, 0, 1]" :key="i" class="calendar-single">
        <h4 class="text-center">
          {{
            new Date(currentYear, currentMonth + monthOffset).toLocaleString(language || "de", {
              month: "long",
              year: "numeric",
            })
          }}
        </h4>
        <div class="calendar-grid">
          <div class="day-name" v-for="day in days" :key="day + i">{{ day }}</div>
          <div
            v-for="(date, index) in getCalendarDays(monthOffset)"
            :key="index"
            :class="['day', date.klasse, { today: date.isToday }]"
          >
            <span class="dot"></span>
            <span>{{ date.isCurrentMonth ? date.day : "" }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Api from "@/api/Api";
import { apiErrorToAlert } from "@/utils/Errorhandler";

export default {
  name: "Kalender",
  props: {
    kalenderDaten: {
      type: Object,
      default: () => ({ tage: [], zeitraeume: [] }),
    },
    von: String,
    bis: String,
  },
  data() {
    return {
      currentMonth: new Date().getMonth(),
      currentYear: new Date().getFullYear(),
      language: null,
    };
  },
  computed: {
    days() {
      switch (this.language) {
        case "en": return ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
        case "fr": return ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];
        case "it": return ["Lu", "Ma", "Me", "Gi", "Ve", "Sa", "Do"];
        default: return ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
      }
    },
    monthName() {
      return new Date(this.currentYear, this.currentMonth).toLocaleString(this.language || "de", {
        month: "long",
      });
    },
    startDate() {
      return new Date(this.von?.split(" ")[0].split(".").reverse().join("-"));
    },
    endDate() {
      return new Date(this.bis?.split(" ")[0].split(".").reverse().join("-"));
    },
    isBeforeStartMonth() {
      const firstVisible = new Date(this.currentYear, this.currentMonth - 1, 1).getTime();
      const start = new Date(this.startDate.getFullYear(), this.startDate.getMonth(), 1).getTime();
      return firstVisible < start;
    },

    isAfterEndMonth() {
      const lastVisible = new Date(this.currentYear, this.currentMonth + 1, 1).getTime();
      const end = new Date(this.endDate.getFullYear(), this.endDate.getMonth(), 1).getTime();
      return lastVisible > end;
    },
  },
  mounted() {
    this.getLanguage();

    const today = new Date();
    if (today < new Date(this.startDate.getFullYear(), this.startDate.getMonth())) {
      this.currentYear = this.startDate.getFullYear();
      this.currentMonth = this.startDate.getMonth();
    } else if (today > new Date(this.endDate.getFullYear(), this.endDate.getMonth(), 31)) {
      this.currentYear = this.endDate.getFullYear();
      this.currentMonth = this.endDate.getMonth();
    }
  },
  methods: {
    getLanguage() {
      return Api.get("aktueller_mitarbeiter/")
        .then((response) => {
          this.language = response.data.lang.kuerzel;
        })
        .catch((e) => {
          this.$notify(apiErrorToAlert(e));
        });
    },
    formatDate(date) {
      return date.toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" });
    },
    getCalendarDays(offset = 0) {
      const year = this.currentYear;
      const month = this.currentMonth + offset;
      const targetDate = new Date(year, month);
      const actualYear = targetDate.getFullYear();
      const actualMonth = targetDate.getMonth();

      const days = [];
      const firstDayOfMonth = new Date(actualYear, actualMonth, 1);
      const startDay = (firstDayOfMonth.getDay() + 6) % 7;
      const daysInMonth = new Date(actualYear, actualMonth + 1, 0).getDate();

      for (let i = 0; i < startDay; i++) {
        days.push({
          day: "",
          isCurrentMonth: false,
          klasse: "",
          isToday: false,
        });
      }

      const today = new Date();
      const todayKey = this.formatDate(today);

      for (let i = 1; i <= daysInMonth; i++) {
        const date = new Date(actualYear, actualMonth, i);
        const dateKey = this.formatDate(date);

        const tag = this.kalenderDaten?.tage?.find?.((t) => t.datum === dateKey);

        let klasse = "no-period";
        if (tag) {
          switch (tag.status) {
            case 5: klasse = "no-period"; break;
            case 4: klasse = "voll"; break;
            case 3: klasse = "reserviert"; break;
            case 2: klasse = "teilweise"; break;
            case 1: klasse = "frei"; break;
            default: klasse = "no-period";
          }
        }

        days.push({
          day: i,
          isCurrentMonth: true,
          klasse,
          isToday: dateKey === todayKey,
        });
      }

      return days;
    },
    prevMonth() {
      if (this.currentMonth === 0) {
        this.currentMonth = 11;
        this.currentYear--;
      } else {
        this.currentMonth--;
      }
    },
    nextMonth() {
      if (this.currentMonth === 11) {
        this.currentMonth = 0;
        this.currentYear++;
      } else {
        this.currentMonth++;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.calendar-wrapper {
  margin: 27px auto 54px;
  text-align: center;
  background: white;
  box-shadow: 0px 0px 10px 4px #c3c3c3;
  border-radius: 4px;
  height: 510px;
  width: 1050px;
  padding: 24px 20px;

  h2 {
    font-weight: 700;
    font-size: 24px;
    padding-bottom: 16px;
  }

  .calendar-header {
    h3 {
      font-size: 16px;
    }

    button.arrow {
      background: none;
      border: none;
      font-size: 1.5rem;
      color: #c8eb24;
    }

    button.arrow:disabled {
      color: #cccccc;
    }
  }

  .calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 5px;

    .day-name {
      font-weight: 600;
      font-size: 12px;
      padding-bottom: 5px;
      color: #808080;
    }

    .day {
      height: 40px;
      width: 40px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      border-radius: 4px;
      font-size: 16px;
      font-weight: 600;
      position: relative;

      &.other-month {
        color: #ccc;
      }

      &.no-period {
        color: #cccccc;
        text-decoration: line-through;
      }

      &.today {
        background: #007bff;
        color: white;
        border-radius: 50%;
      }
    }
  }

  .dot {
    width: 4px;
    height: 4px;
    border-radius: 35%;
    position: absolute;
    top: 4px;
  }

  .voll .dot {
    background: #c92a4a;
  }

  .reserviert .dot {
    background: #ffa600;
  }

  .teilweise .dot {
    background: #49c420;
  }

  .frei .dot {
    background: none;
  }

  .no-period {
    color: #cccccc;
    text-decoration: line-through;
  }

  .kalender-header {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    margin-bottom: 16px;
    margin-top: 18px;
  }

  .kalender-header h2 {
    text-align: center;
  }

  .legende {
    position: absolute;
    right: 0;
    font-size: 0.9rem;
    text-align: left;
    margin: 10px;
    top: -33px;

    .dot {
      position: relative;
      display: inline-block;
      margin-bottom: 4px;
      margin-right: 7px;
    }
  }
}
</style>
